/**
 * Unified "Zlecenia" application — merges the former three pages into one:
 *   • Swipe  → driver's daily route with swipe-to-status (was `/moja-trasa`)
 *   • Lista  → assignment cards + package rows + bulk-status (was `/praca`)
 *   • Mapa   → assignment map viewer + admin reconciliation (was `/deliveries`)
 *
 * The active tab is stored in the `?view=` URL param so the sidebar link can
 * deep-link to a specific mode and the browser back button works naturally.
 * Default tab: `swipe` for drivers, `list` for admin/koordynator/tester.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Hand, ListChecks, Map as MapIcon,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import MyRoute from "./MyRoute";
import WorkPage from "./Work";
import { DeliveriesList } from "./Deliveries";

const TABS = [
  { id: "swipe", label: "Swipe",  icon: Hand,        desc: "Trasa na dziś" },
  { id: "list",  label: "Lista",  icon: ListChecks,  desc: "Wszystkie zlecenia" },
  { id: "map",   label: "Mapa",   icon: MapIcon,     desc: "Rozliczenie · Mapa" },
];

export default function Zlecenia() {
  const { user } = useAuth();
  const isDriver = user?.role === "kierowca";
  const [sp, setSp] = useSearchParams();
  // Driver-facing scope: only Swipe. Admin/tester/koordynator get all 3 tabs.
  const availableTabs = isDriver ? TABS.filter((t) => t.id === "swipe") : TABS;
  const defaultTab = isDriver ? "swipe" : "list";
  const requested = sp.get("view");
  const active = availableTabs.some((t) => t.id === requested) ? requested : defaultTab;

  // Persist default in URL once so refresh/back stays consistent.
  useEffect(() => {
    if (sp.get("view") !== active) {
      const next = new URLSearchParams(sp);
      next.set("view", active);
      setSp(next, { replace: true });
    }
  }, []);

  const setTab = (id) => {
    const next = new URLSearchParams(sp);
    next.set("view", id);
    setSp(next, { replace: false });
  };

  return (
    <div className="p-3 md:p-6 space-y-3" data-testid="zlecenia-page">
      {/* Compact title (per user request: no big hero) */}
      <div className="flex items-baseline gap-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          //
        </div>
        <h1 className="font-heading font-black text-lg uppercase tracking-tight">
          Zlecenia
        </h1>
      </div>

      {/* Tab bar — hidden for drivers (they only have one view). */}
      {availableTabs.length > 1 && (
      <div className="flex gap-2 flex-wrap" data-testid="zlecenia-tabs">
        {availableTabs.map((t) => {
          const isActive = active === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`group inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-heading font-black uppercase tracking-wider transition-all ring-1 ${
                isActive
                  ? "bg-zinc-900 text-white ring-zinc-900 shadow-md"
                  : "bg-white text-zinc-700 ring-zinc-200 hover:ring-zinc-400 hover:bg-zinc-50"
              }`}
            >
              <Icon className={`w-4 h-4 ${isActive ? "text-orange-400" : "text-zinc-500 group-hover:text-orange-500"}`} />
              {t.label}
              <span className={`hidden md:inline text-[10px] font-mono uppercase tracking-widest ${isActive ? "text-white/60" : "text-zinc-400"}`}>
                · {t.desc}
              </span>
            </button>
          );
        })}
      </div>
      )}

      {/* Active view */}
      <TabPane view={active} />
    </div>
  );
}

function TabPane({ view }) {
  // Each pane is fully self-contained: it fetches its own data.
  // Only ONE is mounted at a time; switching remounts (fresh data pull) which
  // is cheap and matches typical driver usage (they pick a mode and stay).
  if (view === "swipe") {
    return <MyRoute embedded />;
  }
  if (view === "list") {
    return <WorkPage embedded />;
  }
  return <DeliveriesList embedded />;
}
